import React, { ReactElement, useEffect, useMemo } from 'react';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  defaultDataIdFromObject,
} from '@apollo/client';
import Head from 'next/head';
import { IntercomProvider } from 'react-use-intercom';

import { Core } from '@reckon-web/core';
import { ToastProvider } from '@reckon-web/toast';
import { DrawerProvider } from '@reckon-web/drawer';
import { UniversalNextLink } from '@reckon-web/next-link';

import {
  AuthProvider,
  authLink,
  errorLink,
  signin,
  useAuth,
  useUser,
} from '../auth';
import { Header } from '../components/header';
import { result as introspectionQueryResultData } from '../../__generated__/ts-gql/@introspection';
import { IntercomComponent } from '../components/Intercom';

const possibleTypes: Record<string, string[]> = {};

introspectionQueryResultData.__schema.types.forEach((supertype: any) => {
  if (supertype.possibleTypes) {
    possibleTypes[supertype.name] = supertype.possibleTypes.map(
      (subtype: any) => subtype.name
    );
  }
});

let ApolloClientProvider = ({ children }: { children: ReactElement }) => {
  let user = useUser();
  const tenant = user.profile.currentTenantId;

  const gqlUrl =
    window.location.origin && window.location.origin.includes('access-aps')
      ? process.env.REACT_APP_GQL_NEW
      : process.env.REACT_APP_GQL;

  return (
    <ApolloProvider
      client={useMemo(() => {
        return new ApolloClient({
          cache: new InMemoryCache({
            possibleTypes,
            dataIdFromObject: (object) => {
              switch (object.__typename) {
                case 'TenantUser':
                  return (object.cloudId || undefined) as string | undefined;
                default:
                  return defaultDataIdFromObject(object);
              }
            },
          }),
          link: ApolloLink.from([
            errorLink,
            authLink(user),
            new HttpLink({
              uri: `${gqlUrl}/${tenant}`,
            }),
          ]),
        });
      }, [user, tenant, gqlUrl])}
    >
      {children}
    </ApolloProvider>
  );
};

let RedirectForAuth = ({ children }: { children: ReactElement }) => {
  let router = useRouter();
  let auth = useAuth();
  useEffect(() => {
    if (auth.type === 'unauthenticated') {
      signin();
    }
  }, [auth, router?.pathname]);

  if (!auth.user) {
    return null;
  }
  return <ApolloClientProvider>{children}</ApolloClientProvider>;
};

export default function App({ Component, pageProps, router }: AppProps) {
  const [manageUserId, setManageUserId] = React.useState<string | null>(null);

  let props = { ...pageProps };
  if (router?.pathname === '/')
    props = { ...pageProps, manageUserId, setManageUserId };

  return (
    <AuthProvider>
      <Head>
        <title>User Management</title>
        <link
          rel="apple-touch-icon"
          sizes="60x60"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />
      </Head>
      <RedirectForAuth>
        <DrawerProvider>
          <ToastProvider>
            <Core linkComponent={UniversalNextLink}>
              {router.pathname === '/login/oauth2/code/cognito' ? (
                <Component {...pageProps} />
              ) : (
                <IntercomProvider appId="c9m53fof">
                  <Header
                    handleSetUserToUpdateId={(user: string | null) =>
                      setManageUserId(user)
                    }
                  />
                  <Component
                    {...props}
                    manageUserId={manageUserId}
                    setManageUserId={setManageUserId}
                  />
                  <IntercomComponent />
                </IntercomProvider>
              )}
            </Core>
          </ToastProvider>
        </DrawerProvider>
      </RedirectForAuth>
    </AuthProvider>
  );
}
